import React, { createContext, useContext, useState, useEffect } from 'react';
import { Product } from '../types/product';
import { useAuth } from './AuthContext';
import { api } from '../services/api';

interface ProductContextType {
  products: Product[];
  addProduct: (formData: FormData) => Promise<void>;
  getProductsByFarmer: (farmerId: string) => Promise<Product[]>;
  deleteProduct: (productId: string) => Promise<void>;
  deactivateProduct: (productId: string) => Promise<void>;
  updateProduct: (productId: string, product: FormData | Partial<Product>) => Promise<void>;
  loading: boolean;
  refreshProducts: () => Promise<void>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Load products from backend on startup or when user changes
  const refreshProducts = async () => {
    try {
      setLoading(true);
      const fetchedProducts = await api.getAllProducts();
      console.log('refreshProducts - fetchedProducts:', fetchedProducts);
      setProducts(fetchedProducts);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshProducts();
  }, [user?.id]); // Re-run when user ID changes

    const addProduct = async (productData: FormData) => {
    if (!user) {
      throw new Error('You must be logged in to add products');
    }

    try {
      const response = await api.createProduct(productData);
      if (!response) {
        throw new Error('Failed to create product');
      }
      
      // Refresh products list
      await refreshProducts();
    } catch (error: any) {
      throw new Error(error.message || 'Failed to add product');
    }
  };

  const getProductsByFarmer = async (farmerId: string): Promise<Product[]> => {
    try {
      console.log('Fetching products for farmer:', farmerId);
      const products = await api.getMyProducts();
      console.log('API Response:', products);
      if (!Array.isArray(products)) {
        console.error('Expected array of products but got:', typeof products);
        return [];
      }
      return products;
    } catch (error) {
      console.error('Failed to fetch farmer products:', error);
      return [];
    }
  };

  const deleteProduct = async (productId: string) => {
    if (!user) {
      throw new Error('You must be logged in to delete products');
    }

    try {
      await api.deleteProduct(productId);
      // Refresh products list
      await refreshProducts();
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete product');
    }
  };

  const deactivateProduct = async (productId: string) => {
    if (!user) {
      throw new Error('You must be logged in to deactivate products');
    }

    try {
      await api.deactivateProduct(productId);
      await refreshProducts();
    } catch (error: any) {
      throw new Error(error.message || 'Failed to deactivate product');
    }
  };

  const updateProduct = async (productId: string, updatedData: FormData | Partial<Product>) => {
    if (!user) {
      throw new Error('You must be logged in to update products');
    }

    try {
      await api.updateProduct(productId, updatedData);
      // Refresh products list
      await refreshProducts();
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update product');
    }
  };

  return (
    <ProductContext.Provider value={{
      products,
      addProduct,
      getProductsByFarmer,
      deleteProduct,
      deactivateProduct,
      updateProduct,
      loading,
      refreshProducts,
    }}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};